import {safeParseJson} from '../../js/tools';

const Transition = {
  FADE: 'fade',
  WIPE: 'wipe'
};

const Effect = {
  ZOOM_IN: 'zoom-in',
};

export class ImageRotator {
  /*
  - there are 2 images: FG & BG
  - FG image = current
  - BG image = next

  Rotation sequence:
  - BG = current, FG = next (hidden)
  - swap images, show BG, hide FG (fades out)
  - fade FG image
  */
  elements = {
    root: null,
    imageContainer: null,
  };
  state = {
    nextEventTime: null,
    imageIndex: 0,
    isPreparedToRotate: false,
    hasFiredRotateHint: false,
  };
  data = {
    items: [],
    delay: 0,
    rotateHintTime: 0,
    transition: '',
    effects: [],
  };
  SCALE_STEP = .0005;
  scaleEffectAnimationFrameCount = 0;
  onRotate = () => { };

  // let's consumer know a rotation will happen shortly
  onRotateHint = () => { }

  constructor(element, { onRotate, onRotateHint } = {}) {
    this.elements = {
      root: element,
      imageContainer: element.querySelector('.image-container'),
    };
    this.onRotate = onRotate;
    this.onRotateHint = onRotateHint;

    // adding image using JS to prevent SEO/a11y issues
    const transitionContainerClone = this.elements.root.querySelector('.image-wrapper')?.cloneNode(true);
    this.elements.imageContainer.append(transitionContainerClone);

    this.data = safeParseJson(this.elements.root.dataset.model);
    this.data.delay = (Number.parseInt(this.data.delay, 10) || 5) * 1000;
    this.data.rotateHintTime = (Number.parseInt(this.data.rotateHintTime, 10) || 1) * 1000;
    this.data.transition = Transition[this.data.transition?.toUpperCase()] || Transition.FADE;
    this.state = {
      nextEventTime: this.data.delay,
      imageIndex: 0,
      isPreparedToRotate: false,
    };

    this.run();
  }

  get foregroundImageWrapper() {
    return this.elements.root.querySelector('div.image-wrapper:last-child');
  }

  get foregroundImage() {
    return this.foregroundImageWrapper.querySelector('img');
  }

  get backgroundImageWrapper() {
    return this.elements.root.querySelector('div.image-wrapper:first-child');
  }

  get backgroundImage() {
    return this.backgroundImageWrapper.querySelector('img');
  }

  delay(duration = 0) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, duration);
    });
  }

  getNextImageIndex() {
    let nextIndex = this.state.imageIndex + 1;
    if (nextIndex > this.data.items.length - 1) {
      nextIndex = 0;
    }
    return nextIndex;
  }

  waitForImageToLoad(image) {
    return new Promise((resolve) => {
      if (image.src && image.complete) {
        // console.log(`image not loaded`);
        resolve();
      } else {
        // console.log(`image not loaded, waiting...`);
        image.addEventListener('load', () => {
          // console.log(`${imageName} loaded`);
          resolve();
        });
      }
    });
  }

  preloadNextImage() {
    const nextImageIndex = this.getNextImageIndex();
    this.foregroundImage.style.opacity = 0;
    this.foregroundImage.src = this.data.items[nextImageIndex].src;
    // foregroundImage.classList.add('loading');
  }

  async prepareToRotate() {
    this.state.isPreparedToRotate = false;
    this.preloadNextImage();
    await this.waitForImageToLoad(this.foregroundImage);
    await this.waitForImageToLoad(this.backgroundImage);
    // this.foregroundImage.classList.remove('loading');
    this.state.isPreparedToRotate = true;
  }

  async fadeTransition() {
    // update link
    this.elements.imageContainer.href = this.data.items[this.state.imageIndex].link;

    // show new image
    this.backgroundImage.style.transition = 'opacity 0s';
    this.backgroundImage.style.opacity = 1;
    this.backgroundImage.style.transform = 'scale(1)';
    this.backgroundImage.$scale = 1;

    // transition out old image
    this.foregroundImage.style.transition = 'opacity 2s';
    await this.delay();
    this.foregroundImage.style.opacity = 0;

    // must wait until transition is complete before preloading new image
    await this.delay(2000);
  }

  async wipeTransition() {
    // update link
    this.elements.imageContainer.href = this.data.items[this.state.imageIndex].link;

    // show new image
    this.resetImageDimensions(this.backgroundImage);
    this.backgroundImage.style.opacity = 1;
    this.backgroundImageWrapper.style.transition = 'width 0s';
    this.backgroundImageWrapper.style.width = '100%';

    // transition out old image
    this.lockImageDimensions(this.foregroundImage);
    this.foregroundImage.style.opacity = 1;
    this.foregroundImageWrapper.style.transition = 'width 1s linear';
    await this.delay();
    this.foregroundImageWrapper.style.width = 0;
    // this.foregroundImageWrapper.style.opacity = 0;

    // must wait until transition is complete before preloading new image
    await this.delay(2000);
  }

  lockImageDimensions(image) {
    const rect = image.getBoundingClientRect();
    image.style.width = `${rect.width}px`;
    image.style.height = `${rect.height}px`;
  }

  resetImageDimensions(image) {
    image.style.width = null;
    image.style.height = null;
  }

  async rotate() {
    this.state.imageIndex = this.getNextImageIndex();
    console.log(`ImageRotator.rotate imageIndex: ${this.state.imageIndex} transition: ${this.data.transition}`);

    this.onRotate(this);
    this.state.hasFiredRotateHint = false; // reset
    this.elements.imageContainer.append(this.backgroundImageWrapper); // swap FG with BG

    let transitionMethodName;

    switch (this.data.transition) {
      case Transition.FADE:
        transitionMethodName = 'fadeTransition';
        break;
      case Transition.WIPE:
        transitionMethodName = 'wipeTransition';
        break;
    }

    return this[transitionMethodName]?.();
  }

  async onAnimationFrame(timeStamp) {
    // console.log({timeStamp, next: this.state.nextEventTime});
    if (!this.state.hasFiredRotateHint && timeStamp >= this.state.nextEventTime - this.data.rotateHintTime) {
      this.onRotateHint(this);
      this.state.hasFiredRotateHint = true;
    }

    if (
      this.state.isPreparedToRotate
      && timeStamp >= this.state.nextEventTime
    ) {
      this.state.nextEventTime = timeStamp + this.data.delay;
      console.log(`event triggered: ${timeStamp}, nextEventTime: ${this.state.nextEventTime}`);
      await this.rotate();
      await this.prepareToRotate();
    }
    window.requestAnimationFrame(this.onAnimationFrame.bind(this));
  }

  applyScaleEffect() {
    // throttle animation to reduce CPU usage
    if (++this.scaleEffectAnimationFrameCount % 4 !== 0) {
      return;
    }
    this.backgroundImage.$scale ||= 1;
    this.backgroundImage.$scale = this.backgroundImage.$scale + this.SCALE_STEP;
    this.backgroundImage.style.transform = `scale(${this.backgroundImage.$scale})`;

    this.foregroundImage.$scale ||= 1;
    this.foregroundImage.$scale = this.foregroundImage.$scale + this.SCALE_STEP;
    this.foregroundImage.style.transform = `scale(${this.foregroundImage.$scale})`;
  }

  onAnimationFrameEffects() {
    if (this.data.effects?.includes(Effect.ZOOM_IN)) {
      this.applyScaleEffect();
    }
    window.requestAnimationFrame(this.onAnimationFrameEffects.bind(this));
  }

  run() {
    window.requestAnimationFrame(this.onAnimationFrame.bind(this));
    window.requestAnimationFrame(this.onAnimationFrameEffects.bind(this));
    this.prepareToRotate();
  }
}
